import { useMutation } from '@tanstack/react-query';
import { getErrorMessage } from '@tectonic/errors';
import { Logger } from '@tectonic/logger';
import { requestOtp, verifyEmailLinking } from '@tectonic/remix-client-network';
import { delay } from '@tectonic/utils';
import { isEmpty } from 'lodash-es';
import { useToast } from '../../../core/ElemasonEntry/Toast';
import { useAccessorValue } from '../../../hooks/useAccessorValue';
import { useAuthStore, useShallow } from '../../../store';
import { setupAnalyticsUser } from '../../../utils';

import type {
  ElemasonAuthEmailLinkVerificationFormWidget,
  PhoneNumber,
  User,
  UserEmailLinkingVerifyPayload,
  UserOtpRequestPayload,
  UserWithAuthTokens,
} from '@tectonic/types';
import type { RefObject } from 'react';
import type { OtpConfirmationFormRef } from '../../../components';
import type { AuthStore } from '../../../store';

const requestOtpMutationFn = async (payload: UserOtpRequestPayload) => {
  const response = await requestOtp(payload);
  if (response.error) {
    throw response.error;
  }
};

const toPhoneText = (phoneNumber: PhoneNumber): string =>
  `${phoneNumber.country.stdCodes[0]}${phoneNumber.phone}`;

const verifyEmailLinkingMutationFn = async (
  payload: UserEmailLinkingVerifyPayload
): Promise<UserWithAuthTokens> => {
  const response = await verifyEmailLinking(payload);
  if (response.error || isEmpty(response.data)) {
    throw response.error;
  }
  return response.data;
};

const authSelector = (state: AuthStore) => ({
  credentials: state.credentials,
  currentMode: state.currentMode,
  emailLinking: state.emailLinking,
});

const useEmailLinkVerificationForm = (
  widget: ElemasonAuthEmailLinkVerificationFormWidget,
  formRef: RefObject<OtpConfirmationFormRef>
) => {
  const wData = widget.data!;

  const { showToast } = useToast();
  const { errorMessages, messages } = wData;
  const { credentials, emailLinking } = useAuthStore(useShallow(authSelector));
  const currentUser = useAccessorValue<User>({
    accessor: '$$globalState.currentUser',
  })!;

  const { isPending: isSendingOtp, mutateAsync: resendOtp } = useMutation({
    mutationKey: [],
    mutationFn: requestOtpMutationFn,
  });

  const { mutateAsync: linkEmail, isPending: isVerifying } = useMutation({
    mutationKey: [],
    mutationFn: verifyEmailLinkingMutationFn,
  });

  const onSubmit = async (code: string) => {
    try {
      const payload = {
        code,
        email: credentials.email!,
        phone: toPhoneText(credentials.phoneNumber!)!,
        phoneVerificationCode: emailLinking.phoneVerificationCode!,
      };
      const { user } = await linkEmail(payload);

      setupAnalyticsUser(user, currentUser);
      showToast({ title: messages.signInSuccess });
      // We want user to see the toast. For now we are using delay. In future,
      // we might use flash message or a better solution for this.
      await delay(1000);
      // Successful login. Let's reload the page.
      globalThis.location.reload();
    } catch (error) {
      Logger.error('useEmailLinkVerificationForm:onSubmit', error);
      showToast({ title: getErrorMessage(error, errorMessages) });
    }
  };

  const onResendOtp = async () => {
    try {
      const { email, phoneNumber } = credentials;
      const phone = phoneNumber ? toPhoneText(phoneNumber) : null;
      await resendOtp({ email, phone });
      showToast({ title: wData.messages.otpResendSuccess });
      formRef.current?.resetTimer();
    } catch (error) {
      Logger.error('useEmailLinkVerificationForm:onResentOtp', error);
      showToast({ title: getErrorMessage(error, errorMessages) });
    }
  };

  const isLoading = isSendingOtp || isVerifying;

  return { isLoading, onSubmit, onResendOtp };
};

export { useEmailLinkVerificationForm };
